import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../shared/Modal';
import StatusPill from '../shared/StatusPill';
import { SelectField, SegmentedField, TextField, TextareaField } from '../shared/FormField';
import { GEOGRAPHIC_SCOPES, SOURCE_ORGANISATIONS } from '../data/enums';
import { useLocationOptions } from '../shared/useLocationOptions';
import { formatDateTime, orDash } from '../shared/format';

function formatActor(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value.name ?? value.label ?? value.uuid ?? null;
  return String(value);
}

function formatActorAt(actor, at) {
  const who = formatActor(actor);
  const when = at ? formatDateTime(at) : null;
  if (!who && !when) return '—';
  if (who && when) return `${who} — ${when}`;
  return who || when;
}

function DetailLine({ label, value }) {
  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-xs text-gray-700">{value}</span>
    </div>
  );
}

const PERIOD_OPTIONS = Array.from({ length: 7 }, (_, i) =>
  ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => ({ value: `${2020 + i}-${q}`, label: `${2020 + i}-${q}` }))
).flat();

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to update dataset.';

export default function EditSupplementaryDatasetModal({
  isOpen,
  onClose,
  onSave,
  row,
  dataSources = [],
  indicatorOptions = [],
}) {
  const [form, setForm] = useState({
    dataset_name: '',
    source_organisation: '',
    indicator_reference: '',
    reference_period: '',
    geographic_scope: '',
    state: '',
    lga: '',
    value_numeric: '',
    notes: '',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultInfo, setResultInfo] = useState({ title: undefined, message: undefined });
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state);

  const currentStatus = (row?.data_status ?? 'draft').toLowerCase();
  // Only a draft/rejected record can meaningfully be "submitted for review".
  const canSubmitForReview = ['draft', 'rejected'].includes(currentStatus);

  // Fall back to static SOURCE_ORGANISATIONS if the backend endpoint returned nothing
  const sourceOptions = useMemo(
    () => (dataSources.length > 0 ? dataSources : SOURCE_ORGANISATIONS),
    [dataSources]
  );

  const set = (key) => (value) => setForm((current) => {
    if (key === 'geographic_scope') {
      const next = { ...current, geographic_scope: value };
      if (String(value).toLowerCase() !== 'state') {
        next.state = '';
        next.lga = '';
      }
      return next;
    }

    if (key === 'state') {
      return { ...current, state: value, lga: '' };
    }

    return { ...current, [key]: value };
  });

  useEffect(() => {
    if (!row) return;

    setForm({
      dataset_name: row.dataset_name ?? '',
      source_organisation: row.source_organisation ?? '',
      indicator_reference: row.indicator_reference ?? '',
      reference_period: row.reference_period ?? '',
      geographic_scope: row.geographic_scope ?? '',
      state: row.state ?? '',
      lga: row.lga ?? '',
      value_numeric: row.value_numeric ?? '',
      notes: row.notes ?? '',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  // Shared field validation + payload builder for both the plain Save and the
  // explicit Submit-for-Review action. Returns null (and sets the error state)
  // when validation fails.
  const buildPayload = (extra = {}) => {
    const numericValue =
      form.value_numeric === '' || form.value_numeric === null || form.value_numeric === undefined
        ? null
        : Number(form.value_numeric);

    if (
      !form.dataset_name.trim() ||
      !form.source_organisation ||
      !form.reference_period ||
      !form.geographic_scope ||
      numericValue === null ||
      Number.isNaN(numericValue)
    ) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return null;
    }

    if (
      String(form.geographic_scope).toLowerCase() === 'state' &&
      !String(form.state || '').trim()
    ) {
      setErrMsg('Please select a state for state-scoped datasets.');
      setStatus('error');
      return null;
    }

    return {
      uuid: row.uuid,
      dataset_name: form.dataset_name.trim(),
      source_organisation: form.source_organisation,
      indicator_reference: form.indicator_reference.trim() || null,
      reference_period: form.reference_period,
      geographic_scope: form.geographic_scope,
      state: form.state.trim() || null,
      lga: form.lga.trim() || null,
      value_numeric: numericValue,
      notes: form.notes.trim() || null,
      ...extra,
    };
  };

  // Default action ("Save Changes") — saves field edits only. It never advances
  // the review status; advancing requires the explicit Submit for Review action.
  const submit = async () => {
    if (submitting || !row?.uuid) return;

    const payload = buildPayload();
    if (!payload) return;

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave(payload);
      setResultInfo({
        title: 'Dataset Updated',
        message: 'The dataset changes were saved to the backend. The review status was not changed.',
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  // Explicit, separate action — saves field edits and moves the dataset into
  // "submitted" via the parent table's handleSave (see submitAfterSave flag).
  const handleSubmitForReview = async () => {
    if (submitting || !row?.uuid) return;

    const payload = buildPayload({ submitAfterSave: true });
    if (!payload) return;

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave(payload);
      setResultInfo({
        title: 'Submitted for Review',
        message: 'Your changes were saved and this dataset was submitted for review.',
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Edit Supplementary Dataset"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle={resultInfo.title}
      successMessage={resultInfo.message}
      isSubmitting={submitting}
      maxWidthClassName="max-w-[820px]"
      bodyGridClassName="grid gap-4 sm:grid-cols-2"
    >
      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:col-span-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-gray-900">Current Status</p>
            <p className="mt-1 text-xs text-gray-500">
              Saving here only updates the fields below — it never changes the review status.
              Validate/Approve/Reject remain available from the details panel.
            </p>
          </div>
          <StatusPill value={row?.data_status || 'draft'} dot />
        </div>
      </div>

      {currentStatus === 'rejected' && row?.rejection_notes && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 sm:col-span-2">
          <p className="text-xs font-bold uppercase tracking-wide text-red-700">Rejection Reason</p>
          <p className="mt-1 text-sm text-red-800">{row.rejection_notes}</p>
        </div>
      )}

      <TextField
        label="Dataset Name"
        required
        value={form.dataset_name}
        onChange={set('dataset_name')}
        placeholder="e.g. National GDP 2024"
      />

      <SelectField
        label="Source Organisation"
        required
        value={form.source_organisation}
        onChange={set('source_organisation')}
        options={sourceOptions}
        placeholder="Select data source"
      />

      {indicatorOptions.length > 0 ? (
        <SelectField
          label="Indicator Reference"
          value={form.indicator_reference}
          onChange={set('indicator_reference')}
          options={indicatorOptions}
          placeholder="Select indicator"
        />
      ) : (
        <TextField
          label="Indicator Reference"
          value={form.indicator_reference}
          onChange={set('indicator_reference')}
          placeholder="e.g. IMP-1"
        />
      )}

      <SelectField
        label="Reference Period"
        required
        value={form.reference_period}
        onChange={set('reference_period')}
        options={PERIOD_OPTIONS}
        placeholder="Select period"
      />

      <div className="sm:col-span-2">
        <SegmentedField
          label="Geographic Scope"
          required
          value={form.geographic_scope}
          onChange={set('geographic_scope')}
          options={GEOGRAPHIC_SCOPES}
        />
      </div>

      <SelectField
        label="State"
        value={form.state}
        onChange={set('state')}
        options={stateOptions}
        placeholder={form.geographic_scope === 'state' ? 'Select State' : 'State not needed'}
        disabled={form.geographic_scope !== 'state'}
        note="Required when geographic scope is State."
      />

      <SelectField
        label="LGA"
        value={form.lga}
        onChange={set('lga')}
        options={lgaOptions}
        placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select State first'}
        disabled={form.geographic_scope !== 'state' || !form.state || loadingLgas}
        note="Optional for state-scoped records."
      />

      <TextField
        label="Value Numeric"
        required
        type="number"
        step="any"
        inputMode="decimal"
        value={form.value_numeric}
        onChange={set('value_numeric')}
        placeholder="Enter numeric value"
        note="This value is sent to the backend as a number."
      />

      <div className="sm:col-span-2">
        <TextareaField
          label="Notes"
          value={form.notes}
          onChange={set('notes')}
          placeholder="Optional notes about the source or calculation"
          rows={3}
        />
      </div>

      {row?.uuid && (
        <div className="grid gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:col-span-2 sm:grid-cols-3">
          <p className="text-[13px] font-semibold text-gray-900 sm:col-span-3">Record Trail</p>
          <DetailLine label="Submitted" value={formatActorAt(row?.submitted_by, row?.submitted_at)} />
          <DetailLine label="Validated" value={formatActorAt(row?.validated_by, row?.validated_at)} />
          <DetailLine label="Approved" value={formatActorAt(row?.approved_by, row?.approved_at)} />
          <DetailLine label="Rejected At" value={row?.rejected_at ? formatDateTime(row.rejected_at) : '—'} />
          <DetailLine label="Created At" value={row?.created_at ? formatDateTime(row.created_at) : '—'} />
          <DetailLine label="Updated At" value={row?.updated_at ? formatDateTime(row.updated_at) : '—'} />
          <DetailLine label="UUID" value={orDash(row?.uuid)} />
        </div>
      )}

      {row?.uuid && canSubmitForReview && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 sm:col-span-2">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-blue-900">Ready for review?</p>
            <p className="mt-1 text-xs text-blue-700">Saves your changes, then submits this dataset for review.</p>
          </div>
          <button
            type="button"
            onClick={handleSubmitForReview}
            disabled={submitting}
            className="h-11 shrink-0 rounded-full bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      )}
    </Modal>
  );
}
